import React from 'react';

import Metadata from '@pray/shared/components/Metadata';

import { Hourglass, Globus, Microphone } from 'components/pages/public/components/icons/benefits';
import { AI } from 'components/pages/public/components/icons/features';
import { PRODUCT_LABELS, Products } from 'components/pages/public/types/products';
import { VIEW_EVENT_NAMES } from 'constants.js';

import { Header } from '../../../../components/Header/Header';
import ProductPageLayout from '../../layout/ProductPageLayout';

const WorkflowAIPage: React.FC = () => {
  return (
    <div data-viewname={VIEW_EVENT_NAMES.PRODUCTS.WORKFLOW_AI}>
      <Metadata
        title={`Automate Your Ministry & Grow | ${PRODUCT_LABELS[Products.PRAYSTUDIO]}`}
        description={`Boost your ministry with ${
          PRODUCT_LABELS[Products.WORKFLOW_AI]
        }. Automate sermons, translate content, and grow your reach effortlessly.`}
      />
      <Header />
      <ProductPageLayout
        // Hero section
        tag={PRODUCT_LABELS[Products.WORKFLOW_AI]}
        icon={<AI />} // Using AI icon for Hero
        title="Equip Your Ministry with AI"
        description="Workflow AI is your all-in-one intelligent ministry assistant."
        ctaText="GET STARTED"
        heroMedia={{ type: 'image', src: 'https://d339bgihw9yfmi.cloudfront.net/web/images/workflowai-hero.png' }}
        // Benefits section
        benefitsSectionTitle={`Why ${PRODUCT_LABELS[Products.WORKFLOW_AI]}?`}
        benefits={[
          {
            icon: <Hourglass />,
            title: 'Save Time',
            description: `No fancy editing skills needed. ${
              PRODUCT_LABELS[Products.WORKFLOW_AI]
            } does the heavy lifting for you.`,
            linkText: 'LEARN MORE',
          },
          {
            icon: <Globus />,
            title: 'Automate Your Social Media',
            description: 'Grow your audience on social media with Social Clips.',
            linkText: 'LEARN MORE',
          },
          {
            icon: <Microphone />,
            title: 'Automate Your Emails',
            description: 'Turn your boring emails into engaging ones automatically.',
            linkText: 'LEARN MORE',
          },
          {
            icon: <AI />,
            title: 'Automate Your Announcements',
            description: 'Keep your community informed with announcements.',
            linkText: 'LEARN MORE',
          },
        ]}
        howItWorksSectionTitle={`What ${PRODUCT_LABELS[Products.WORKFLOW_AI]} Can Do for You`}
        steps={[
          {
            title: 'Automatic Sermon Summarization',
            description: `Upload your sermon, and ${
              PRODUCT_LABELS[Products.WORKFLOW_AI]
            } generates easy-to-share social media quotes, Bible verses, and highlights to inspire your congregation.`,
            buttonText: 'GET STARTED',
            imageSrc: 'https://d339bgihw9yfmi.cloudfront.net/web/images/workflowai-benefit-step-1.png',
            imageAlt: 'Automatic Sermon Summarization',
          },
          {
            title: 'Social Media Clips Made Easy',
            description:
              'Create engaging, shareable posts instantly with viral video clips, tweets, descriptions, and tags personalized to your message. Schedule content seamlessly across your favorite social media platforms.',
            buttonText: 'GET STARTED',
            imageSrc: 'https://d339bgihw9yfmi.cloudfront.net/web/images/workflowai-benefit-step-2.png',
            imageAlt: 'Social Media Clips Made Easy',
          },
          {
            title: 'Personalize with Voice Translation',
            description:
              'Maintain your personal touch—-your voice is translated into multiple languages, making your sermons accessible to more people across the globe.',
            buttonText: 'GET STARTED',
            imageSrc: 'https://d339bgihw9yfmi.cloudfront.net/web/images/workflowai-benefit-step-3.png',
            imageAlt: 'Personalize with Voice Translation',
          },
          {
            title: 'Email & Announcement Automation',
            description: `Keep your flock informed. ${
              PRODUCT_LABELS[Products.WORKFLOW_AI]
            } automatically generates personalized emails and announcements, ensuring your weekly messages are at the forefront of their minds.`,
            buttonText: 'GET STARTED',
            imageSrc: 'https://d339bgihw9yfmi.cloudfront.net/web/images/workflowai-benefit-step-4.png',
            imageAlt: 'Email & Announcement Automation',
          },
          {
            title: 'Blog & Study Guides',
            description: `Expand your teaching beyond Sunday services. ${
              PRODUCT_LABELS[Products.WORKFLOW_AI]
            } automatically generates captivating blog articles and study guides based on your sermons, helping your congregation grow in faith during the week.`,
            buttonText: 'GET STARTED',
            imageSrc: 'https://d339bgihw9yfmi.cloudfront.net/web/images/workflowai-benefit-step-5.png',
            imageAlt: 'Blog & Study Guides',
          },
        ]}
        // Solution cards section (using placeholders as before)
        solutionsSectionTitle="All-in-one Solution"
      />
    </div>
  );
};

export default WorkflowAIPage;
